import Tooltip from "@mui/material/Tooltip";
import type { MouseEvent, ReactNode } from "react";
import { useCallback } from "react";

/**
 * Icon-only round action floated over the Step 4 preview shell ("Send a test email",
 * "Edit the design"). Mirrors the prototype's `.review-icon-btn`
 * (Campaign-Builder-Master-Prototype.html, CSS ~line 7254 onward), introduced by the
 * Stage 2 revisions when the Recipient Experience card lost its header.
 *
 * The prototype draws the tooltip with a `::after` on `data-tooltip`; here it is MUI's
 * `Tooltip` instead, so the label is announced rather than painted as pseudo-content.
 * `label` doubles as the accessible name — there is no visible text.
 */
export interface ReviewIconButtonProps {
  /** Tooltip copy and `aria-label` — the button renders no visible text. */
  label: string;
  icon: ReactNode;
  onClick: () => void;
}

export default function ReviewIconButton({ label, icon, onClick }: ReviewIconButtonProps) {
  // The button sits inside the preview shell, which is itself a click target that opens
  // the fullscreen preview. Stopping propagation here (as the prototype does inline on
  // each `onclick`) keeps every caller from having to remember it.
  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onClick();
    },
    [onClick],
  );

  return (
    <Tooltip title={label} placement="bottom-end">
      <button
        type="button"
        aria-label={label}
        onClick={handleClick}
        className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border-none bg-white/92 text-rodeo-black shadow-[0_2px_8px_rgba(0,0,0,0.18)] transition-[background-color,transform] duration-150 hover:-translate-y-px hover:bg-white"
      >
        {icon}
      </button>
    </Tooltip>
  );
}
